import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import adminServices from '../../services/AdminServices'
import moment from 'moment'

function BookingModal(
    {
        isOpen, onClose, mode = 'create',
        data = null, propertyId = null,
        nightlyRate = 0,
    }
) {
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [formData, setFormData] = useState({
        full_name: '',
        user_email: '',
        guests: 1,
        check_in: '',
        check_out: '',
        notes: ''
    })

    const [calculatedPrice, setCalculatedPrice] = useState(0)
    const [nights, setNights] = useState(0)

    useEffect(() => {
        if (mode === 'update' && data) {
            setFormData({
                full_name: data.full_name || '',
                user_email: data.user_email || '',
                guests: data.guests || 1,
                check_in: formatDateToInput(data.check_in),
                check_out: formatDateToInput(data.check_out),
                notes: data.notes || ''
            })

            if (data.total_price) {
                setCalculatedPrice(parseFloat(data.total_price))
            }
        } else {

            resetForm()
        }
    }, [isOpen, mode, data])

    useEffect(() => {
        if (formData.check_in && formData.check_out) {
            const startDate = new Date(formData.check_in)
            const endDate = new Date(formData.check_out)
            const diffTime = Math.abs(endDate - startDate)
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

            if (diffDays > 0) {
                setNights(diffDays)
                if (mode === 'create') {
                    setCalculatedPrice(nightlyRate * diffDays)
                }
            } else {
                if (mode === 'create') {
                    setNights(0)
                    setCalculatedPrice(0)
                }
            }
        }
    }, [formData.check_in, formData.check_out, nightlyRate, mode])

    const resetForm = () => {
        setFormData({
            full_name: '',
            user_email: '',
            guests: 1,
            check_in: '',
            check_out: '',
            notes: ''
        })
        setCalculatedPrice(0)
        setNights(0)
        setError(null)
    }

    const formatDateToInput = (dateStr) => {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        return date.toISOString().split('T')[0]
    }

    const getMinCheckOutDate = () => {
        if (!formData.check_in) return new Date().toISOString().split('T')[0]

        const tomorrow = new Date(formData.check_in)
        tomorrow.setDate(tomorrow.getDate() + 1)
        return tomorrow.toISOString().split('T')[0]
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        setSaving(true)

        try {
            const payload = {
                ...formData,
                guests: parseInt(formData.guests) || 1
            }

            let response
            if (mode === 'create') {
                payload.property = propertyId
                response = await adminServices.createBooking(payload)
                console.log(response);

                toast.success('Booking submitted successfully! We will contact you shortly.')
            } else {
                response = await adminServices.updateBooking(data.id, { ...payload, status: payload.status || 'pending' })
                console.log(response);

                toast.success('Booking updated successfully')
            }

            setTimeout(() => {
                onClose?.()
                resetForm()
                // Trigger parent reload if available
            }, 1500)
        } catch (err) {
            const msg = err.response?.data?.error
                || err.response?.data?.non_field_errors?.[0]
                || 'Operation failed. Please try again.'
            setError(msg)
            toast.error(msg)
        } finally {
            setSaving(false)
        }
    }

    if (!isOpen) return null

    const isInvalid = !formData.full_name || !formData.user_email || !formData.check_in || !formData.check_out

    return (
        <div className="modal modal-open">
            <div className="modal-box w-11/12 max-w-lg">
                <h3 className="font-bold text-xl mb-4 text-primary">
                    {mode === 'create' ? 'Book Your Stay' : 'Edit Booking'}
                </h3>
                <label
                    className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    onClick={() => {
                        onClose?.()
                    }}
                >
                    ✕
                </label>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="alert alert-error text-sm">
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label"><span className="label-text font-semibold">Full Name *</span></label>
                            <input
                                type="text"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                required
                                className="input input-bordered focus:border-primary"
                                placeholder="John Doe"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label"><span className="label-text font-semibold">Email *</span></label>
                            <input
                                type="email"
                                name="user_email"
                                value={formData.user_email}
                                onChange={handleChange}
                                required
                                className="input input-bordered focus:border-primary"
                                placeholder="john@example.com"
                            />
                        </div>
                    </div>

                    <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Guests</span></label>
                        <input
                            type="number"
                            name="guests"
                            min="1"
                            value={formData.guests}
                            onChange={handleChange}
                            className="input input-bordered focus:border-primary"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label"><span className="label-text font-semibold">Check-in *</span></label>
                            <input
                                type="date"
                                name="check_in"
                                min={new Date().toISOString().split('T')[0]}
                                value={formData.check_in}
                                onChange={handleChange}
                                required
                                className="input input-bordered focus:border-primary"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label"><span className="label-text font-semibold">Check-out *</span></label>
                            <input
                                type="date"
                                name="check_out"
                                min={getMinCheckOutDate()}
                                value={formData.check_out}
                                onChange={handleChange}
                                required
                                className="input input-bordered focus:border-primary"
                            />
                        </div>
                    </div>

                    {(nights > 0 || calculatedPrice > 0) && (
                        <div className="bg-surface-container rounded-lg p-4 border border-outline-variant/10">
                            <div className="flex justify-between items-center text-sm mb-1">
                                <span className="text-on-surface-variant/70">{nights} Night{Math.abs(nights) > 1 ? 's' : ''}</span>
                                {mode === 'create' && (
                                    <span className="text-primary font-medium">{nightlyRate.toLocaleString()} KES × {nights}</span>
                                )}
                                {mode === 'update' && (
                                    <span className="text-on-surface-variant/60 text-xs italic">{data?.property?.nightly_rate}</span>
                                )}
                            </div>
                            <div className="border-t border-outline-variant/10 my-2"></div>
                            <div className="flex justify-between items-center font-bold text-lg">
                                <span>Total</span>
                                <span className="text-secondary">{calculatedPrice.toLocaleString()} KES</span>
                            </div>
                            <div>
                                {mode === 'update' && (
                                    <div className="text-on-surface-variant/60 text-sm mt-3">
                                        Created on: {data.created_at ? moment(data.created_at).format('MMM Do, YYYY') : '—'} <br />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 mt-6">
                        <button type="submit" disabled={saving || isInvalid} className="btn btn-primary flex-1">
                            {saving ? (
                                <>
                                    <span className="loading loading-spinner loading-xs"></span>
                                    {mode === 'create' ? 'Processing...' : 'Updating...'}
                                </>
                            ) : mode === 'create' ? 'Confirm Booking' : 'Save Changes'}
                        </button>
                        <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
                    </div>
                </form>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={() => { onClose?.(); resetForm() }} className="outline-none">close</button>
            </form>
        </div>
    )
}

export default BookingModal